import type { OnResourceSaveCallback } from '@ctw/shared/api/fhir/action-helper';
import { getFetchFromFqs } from '@ctw/shared/api/fqs/client';
import { notify } from '@ctw/shared/components/toast';
import { UserPatientSummarizationProvider } from '@ctw/shared/content/summarization/user-patient-summarization-context';
import { BuilderConfigProvider } from '@ctw/shared/context/builder-config-context';
import { DrawerProvider } from '@ctw/shared/context/drawer-context';
import { ModalProvider } from '@ctw/shared/context/modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { TelemetryInitializer } from '@ctw/shared/context/telemetry/telemetry-initializer';
import {
  WritebackProvider,
  type WritebackProviderProps,
} from '@ctw/shared/context/writeback-provider';
import type { ZusService } from '@ctw/shared/context/zui-provider';
import { useBrowserCompatibility } from '@ctw/shared/hooks/use-browser-compatibility';
import {
  type ZusAuthTokenState,
  getAuthTokenState,
  isAuthTokenExpiringWithin,
  isAuthTokenPastExpiryGracePeriod,
} from '@ctw/shared/utils/auth';
import {
  type Env,
  currentHostnameIsLocalDevEnvironment,
  getApiServiceBaseUrl,
  getEnvFromCurrentHostname,
} from '@ctw/shared/utils/environments';
import { RequestError } from '@ctw/shared/utils/error';
import { assertResponseOK, getResponseContent } from '@ctw/shared/utils/http';
import { QueryCache, QueryClient } from '@tanstack/query-core';
import { QueryClientProvider, type QueryClientProviderProps } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { GraphQLClient } from 'graphql-request';
import { debounce, upperFirst } from 'lodash-es';
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { AUTH_TOKEN_REFRESH_BUFFER_SECONDS } from '../utils/auth-token-refresh';
import { BulkActionProvider } from './bulk-action-provider';
import { FeatureFlagProvider, type UnleashFeatureOverrides } from './feature-flag-provider';
import { ToastProvider } from './toast-provider';

// We use an expiry padding to provide a buffer to prevent race conditions.
// A race condition could happen in that we check if the token is expired,
// it isn't, but by time we do our request(s) it has expired.
// We track the id of the toast that we open to avoid stacking.
const AUTH_ERROR_TOAST_ID = 'invalid-token' as const;

export const CTW_REQUEST_HEADER = { 'Zus-Request-Source': 'component-library' } as const;

export type CtwFetchFn = <TData>(
  url: Parameters<typeof fetch>[0],
  init: Parameters<typeof fetch>[1],
) => Promise<{
  data: TData;
  ok: boolean;
  headers: Response['headers'];
}>;

export interface CTWRequestContext {
  isInIframe: boolean;
  env: Env;
  headers?: HeadersInit;
  authToken: string;
  ehr: string;
  builderId: string;
  service: ZusService;
  serviceVersion: string;
  serviceVariant?: string;
  serviceEnv: Env;
  isAuthTokenExpired: () => boolean;
  authTokenState: ZusAuthTokenState;
  ehrContext?: Record<string, string>;
}

export interface CTWState {
  requestContext: CTWRequestContext;
  headers?: HeadersInit;
  allowSmallBreakpointCCDAViewer: boolean;
  ctwBehavioralFlags: Record<string, boolean>;
  onResourceSave?: OnResourceSaveCallback;
  ehrContext?: Record<string, string>;
  updateBuilderId: (builderId: string) => void;
  graphqlClient: GraphQLClient;
  /*
   * @deprecated This method should not be used and will be replaced soon.
   */
  fetchFromFqs: (
    url: string,
    options: RequestInit,
  ) => Promise<{ data: unknown; headers: Response['headers'] }>;
  ctwFetch: CtwFetchFn;
}

export const CTWContext = createContext<CTWState | undefined>(undefined);

export interface CTWDevProps {
  fallbackEnv: Env;
}

export type CTWProviderProps = {
  devProps?: CTWDevProps;
  service: ZusService;
  serviceVersion: string;
  serviceVariant?: string;
  builderId?: string;
  ehr: string;
  headers?: Record<string, string>;
  authToken: string;
  getRefreshedAuthToken?: (token: string) => Promise<string>;
  onAuthTokenExpiration?: () => void;
  ctwBehavioralFlags?: Record<string, boolean>;
  unleashFeatureOverrides?: UnleashFeatureOverrides;
  onResourceSave?: OnResourceSaveCallback;
  writebacks?: WritebackProviderProps;
  ehrContext?: Record<string, string>;
  allowSmallBreakpointCCDAViewer?: boolean;
};

/**
 * CTWProvider is required for all apps in the monorepo. It should be loaded as early as possible
 * and provides global context and telemetry for the app.
 */
export const CTWProvider = ({
  devProps,
  children,
  builderId,
  ehr,
  service,
  serviceVersion,
  serviceVariant,
  ctwBehavioralFlags = {},
  unleashFeatureOverrides = {},
  headers,
  authToken: initialAuthToken,
  onResourceSave,
  getRefreshedAuthToken,
  onAuthTokenExpiration,
  ehrContext,
  allowSmallBreakpointCCDAViewer = false,
  writebacks,
}: PropsWithChildren<CTWProviderProps>) => {
  const telemetry = useTelemetry();
  const telemetryRef = useRef(telemetry);
  telemetryRef.current = telemetry;
  const [authToken, setAuthToken] = useState(initialAuthToken);
  const [authTokenState, setAuthTokenState] = useState(getAuthTokenState(authToken));
  const [currentBuilderId, setCurrentBuilderId] = useState(builderId ?? authTokenState.builderId);
  const [showingAuthTokenError, setShowingAuthTokenError] = useState(false);
  const [browserWarningShown, setBrowserWarningShown] = useState(false);
  const { isBrowserSupported, SUPPORTED_BROWSERS } = useBrowserCompatibility();
  const env = useMemo(
    () => getEnvFromCurrentHostname({ fallbackEnv: devProps?.fallbackEnv }),
    [devProps],
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: The linter complains that getRefreshedAuthToken is not needed, but we do need to re-initialize this callback when it changes.
  const debouncedGetRefreshedAuthToken = useCallback(
    debounce(async (token) => getRefreshedAuthToken?.(token), 2000, { leading: true }),
    [getRefreshedAuthToken],
  );

  useEffect(() => {
    telemetry.logger.debug('Mounting CTWProvider', {
      env,
      ehr,
      builderId,
      service,
      serviceVersion,
      serviceVariant,
      ctwBehavioralFlags,
    });
  }, [
    builderId,
    ehr,
    env,
    ctwBehavioralFlags,
    service,
    serviceVariant,
    serviceVersion,
    telemetry.logger,
  ]);

  // The QueryClient must be created exactly once — recreating it destroys
  // the entire query cache and restarts every active query (including
  // long-running fqsRequestAll pagination loops).  We access telemetry
  // through a ref so the error handler always uses the latest instance
  // without being a useMemo dependency.
  const queryClient: QueryClientProviderProps['client'] = useMemo(
    (): QueryClientProviderProps['client'] =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: false,
            gcTime: 1000 * 30, // garbage collect data every 30s
            staleTime: 1000 * 30, // data is valid for 30s, stale while revalidate after
            refetchOnWindowFocus: false,
          },
        },
        queryCache: new QueryCache({
          onError: (error, query) => {
            telemetryRef.current.trackError({
              message: 'Unhandled react query error',
              error,
              context: {
                query: {
                  key: query.queryKey,
                  status: query.state.status,
                },
                queryMeta: query.meta,
              },
            });
          },
        }),
      }) as unknown as QueryClientProviderProps['client'],
    [],
  );

  const requestContext: CTWRequestContext = useMemo(
    () => ({
      isInIframe: window.location !== window.parent.location,
      authToken,
      isAuthTokenExpired: () => isAuthTokenPastExpiryGracePeriod(authToken),
      authTokenState,
      builderId: currentBuilderId,
      ehr,
      env,
      headers,
      service,
      serviceEnv: currentHostnameIsLocalDevEnvironment() ? 'dev' : env,
      serviceVersion,
      serviceVariant,
      ehrContext,
    }),
    [
      authToken,
      authTokenState,
      currentBuilderId,
      ehr,
      env,
      headers,
      service,
      serviceVariant,
      serviceVersion,
      ehrContext,
    ],
  );

  const graphqlClient = useMemo(() => {
    const endpoint = `${getApiServiceBaseUrl({ service: 'fqs', env: requestContext.env })}/query`;

    return new GraphQLClient(endpoint, {
      errorPolicy: 'all',
      headers: {
        ...CTW_REQUEST_HEADER,
        authorization: `Bearer ${requestContext.authToken}`,
      },
    });
  }, [requestContext.authToken, requestContext.env]);

  const ctwFetch: CtwFetchFn = useCallback(
    async (input, init = {}) => {
      const newInit: Parameters<typeof fetch>[1] = {
        ...init,
        headers: {
          ...init.headers,
          ...CTW_REQUEST_HEADER,
        },
      };

      try {
        const fetchResponse = await fetch(input, newInit);
        await assertResponseOK(fetchResponse);

        return {
          data: await getResponseContent(fetchResponse),
          ok: fetchResponse.ok,
          headers: fetchResponse.headers,
        };
      } catch (error) {
        telemetry.trackError({
          error,
          context: {
            fetch: {
              url: input.toString(),
              init: {
                ...newInit,
                headers: telemetry.redactSensitiveHttpHeaders(newInit.headers),
              },
            },
          },
        });

        return {
          data: error instanceof RequestError ? error.data : {},
          ok: false,
          headers: new Headers(),
        };
      }
    },
    [telemetry],
  );

  const providerState: CTWState = useMemo(
    () => ({
      requestContext,
      allowSmallBreakpointCCDAViewer,
      ctwBehavioralFlags,
      headers,
      onResourceSave,
      ehrContext,
      updateBuilderId: setCurrentBuilderId,
      fetchFromFqs: getFetchFromFqs(ctwFetch, env, authToken),
      graphqlClient,
      ctwFetch,
    }),
    [
      requestContext,
      allowSmallBreakpointCCDAViewer,
      ctwBehavioralFlags,
      headers,
      onResourceSave,
      ehrContext,
      env,
      authToken,
      graphqlClient,
      ctwFetch,
    ],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: The linter complains that the getRefreshedAuthToken function is not needed, but it is.
  useLayoutEffect(() => {
    const intervalId = setInterval(() => {
      const handleAuthTokenRefresh = async () => {
        if (isAuthTokenExpiringWithin(authToken, AUTH_TOKEN_REFRESH_BUFFER_SECONDS)) {
          const refreshedAuthToken = await debouncedGetRefreshedAuthToken(authToken);
          const refreshedTokenState = getAuthTokenState(refreshedAuthToken ?? '');

          if (refreshedAuthToken && refreshedAuthToken !== authToken) {
            setAuthToken(refreshedAuthToken);
            setAuthTokenState(refreshedTokenState);
          } else if (
            (!(refreshedAuthToken && refreshedTokenState.email) ||
              isAuthTokenPastExpiryGracePeriod(refreshedAuthToken)) &&
            !showingAuthTokenError
          ) {
            onAuthTokenExpiration?.();
            sendInvalidTokenNotification(providerState.requestContext, AUTH_ERROR_TOAST_ID);
            setShowingAuthTokenError(true);
          }
        }
      };

      void handleAuthTokenRefresh();
    }, 5_000);

    return () => {
      // Ensure the last enqueued execution of the interval finishes before clearing it
      setTimeout(() => {
        clearInterval(intervalId);
      }, 1000);
    };
  }, [
    authToken,
    debouncedGetRefreshedAuthToken,
    getRefreshedAuthToken,
    onAuthTokenExpiration,
    providerState.requestContext,
    showingAuthTokenError,
  ]);

  useLayoutEffect(() => {
    // We don't want to stack toast notifications. So we track if there is one already open with auth errors
    const unsubscribe = toast.onChange((t) => {
      if (t.id === AUTH_ERROR_TOAST_ID && t.status === 'removed') {
        setShowingAuthTokenError(false);
      }
    });
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    // Check browser compatibility
    if (!(browserWarningShown || isBrowserSupported())) {
      sendUnsupportedBrowserNotification();
      setBrowserWarningShown(true);
      telemetry.logger.info('Detected unsupported browser');
    }
  }, [isBrowserSupported, browserWarningShown, telemetry.logger]);

  const sendUnsupportedBrowserNotification = useCallback(() => {
    const browsersToDisplay = Array.from(SUPPORTED_BROWSERS)
      .filter((b) => !(b.startsWith('ios_') || b.startsWith('and_'))) // Filter out ugly iOS / Android browser strings
      .map(upperFirst);
    let browserList: string;
    switch (browsersToDisplay.length) {
      case 0:
        browserList = 'a supported browser';
        break;
      case 1:
        browserList = browsersToDisplay[0];
        break;
      case 2:
        browserList = `${browsersToDisplay[0]} or ${browsersToDisplay[1]}`;
        break;
      default:
        browserList = `${browsersToDisplay
          .slice(0, -1)
          .join(', ')}, or ${browsersToDisplay.slice(-1)}`;
    }

    notify({
      type: 'error',
      title: 'Unsupported Browser',
      body: `You are using an unsupported browser. For the best experience, please use the latest version of ${browserList}.`,
      options: {
        toastId: 'unsupported-browser',
      },
    });
  }, [SUPPORTED_BROWSERS]);

  return (
    <CTWContext.Provider value={providerState}>
      <TelemetryInitializer>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} />
          <ToastProvider>
            <FeatureFlagProvider env={env} unleashFeatureOverrides={unleashFeatureOverrides}>
              <WritebackProvider {...writebacks}>
                <BulkActionProvider>
                  <BuilderConfigProvider>
                    <ModalProvider>
                      {({ modal }) => (
                        <UserPatientSummarizationProvider>
                          <DrawerProvider allowDocking={false}>
                            {({ drawer }) => (
                              <>
                                {drawer}
                                {children}
                              </>
                            )}
                          </DrawerProvider>
                          {modal}
                        </UserPatientSummarizationProvider>
                      )}
                    </ModalProvider>
                  </BuilderConfigProvider>
                </BulkActionProvider>
              </WritebackProvider>
            </FeatureFlagProvider>
          </ToastProvider>
        </QueryClientProvider>
      </TelemetryInitializer>
    </CTWContext.Provider>
  );
};

const sendInvalidTokenNotification = (requestContext: CTWRequestContext, toastId: string) => {
  const message = requestContext.isAuthTokenExpired()
    ? 'This token has expired'
    : 'Only user tokens are allowed';
  notify({
    type: 'error',
    title: 'Warning',
    body: `Invalid auth token: ${message}`,
    options: {
      toastId,
    },
  });
};

export const useCTW = () => {
  const context = useContext(CTWContext);

  if (!context) {
    throw new Error('useCTW must be used within a CTWProvider');
  }

  return context;
};
